import { UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInvalidateIntegrations } from '@components/integrations';
import {
  EditPollingVisibilityType,
  IntegrationPollingType,
} from '@components/integrations/integrations-types';
import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { isNotFoundError } from '@utils/requests';

import { isInTemplateRoute } from 'ui/router';

import {
  editPollingVisibility,
  generateJqQuery,
  listPollingEndpoints,
  listSyncTestRequest,
  loadIntegrationJourneys,
  loadIntegrationTravellerActionFile,
  loadJourneyIntegrationEndpoints,
  loadJourneyIntegrationWebhooks,
  loadTemplateJourneyIntegrationEndpoints,
  loadTemplateJourneyIntegrationWebhooks,
  loadTravellerActionIntegrationEndpoint,
  loadTravellerJourneyIntegrationEndpoint,
  resubscribeMarketplaceWebhook,
  validateJqQuery,
} from './requests';

const getJourneyIntegrationEndpointsQueryKey = (journeyId: string) => [
  'journey-integration-endpoints',
  journeyId,
];

const getJourneyIntegrationWebhooksQueryKey = (journeyId: string) => [
  'journey-integration-webhooks',
  journeyId,
];

export const useJourneyIntegrationEndpoints = (shortId: string) => {
  const loadFn = isInTemplateRoute()
    ? loadTemplateJourneyIntegrationEndpoints
    : loadJourneyIntegrationEndpoints;
  return useQuery({
    queryKey: getJourneyIntegrationEndpointsQueryKey(shortId),
    queryFn: () => loadFn(shortId),
    enabled: Boolean(shortId),
  });
};

export const useIntegrationJourneys = (shortId: string) => {
  return useQuery({
    queryKey: ['integration-journeys', shortId],
    queryFn: () => loadIntegrationJourneys(shortId),
    enabled: Boolean(shortId),
  });
};

export const useTravellerJourneyIntegrationEndpoint = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-journey-integration-endpoints', shortId],
    queryFn: () => loadTravellerJourneyIntegrationEndpoint(shortId),
  });
};

export const useTravellerActionIntegrationEndpoint = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-action-integration-endpoints', shortId],
    queryFn: () => loadTravellerActionIntegrationEndpoint(shortId),
    // A deleted endpoint never comes back; transient failures may still refetch.
    refetchOnWindowFocus: (query) => !isNotFoundError(query.state.error),
    refetchOnReconnect: (query) => !isNotFoundError(query.state.error),
  });
};

export const useJourneyIntegrationWebhook = (shortId: string) => {
  const loadFn = isInTemplateRoute()
    ? loadTemplateJourneyIntegrationWebhooks
    : loadJourneyIntegrationWebhooks;
  return useQuery({
    queryKey: getJourneyIntegrationWebhooksQueryKey(shortId),
    queryFn: () => loadFn(shortId),
    enabled: Boolean(shortId),
  });
};

export const useInvalidateJourneyIntegrations = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyIntegrationEndpoints = (journeyId: string) => {
    return queryClient.invalidateQueries({
      queryKey: getJourneyIntegrationEndpointsQueryKey(journeyId),
    });
  };

  const invalidateJourneyIntegrationWebhooks = (journeyId: string) => {
    return queryClient.invalidateQueries({
      queryKey: getJourneyIntegrationWebhooksQueryKey(journeyId),
    });
  };

  return { invalidateJourneyIntegrationEndpoints, invalidateJourneyIntegrationWebhooks };
};

export const useIntegrationTravellerActionFile = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-action-integration-file', shortId],
    queryFn: () => loadIntegrationTravellerActionFile(shortId),
    enabled: Boolean(shortId),
  });
};

export const useListSyncTestRequest = (integrationShortId: string) => {
  const { invalidateIntegrations } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (data: { url: string }) => listSyncTestRequest(integrationShortId, data),
    onSuccess: () => invalidateIntegrations(),
  });
};

export const useEditPollingVisibility = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: EditPollingVisibilityType) => editPollingVisibility(data),
    onSuccess: () => {
      invalidateIntegrations();
      showSuccess(t('pollVisibilitySuccess'));
    },
  });
};

export const useListPollingEndpoints = (
  journeyId: string,
  options?: UseQueryOptions<IntegrationPollingType[]>,
) => {
  return useQuery({
    queryKey: ['integrations', journeyId, 'polling-endpoints'],
    queryFn: () => listPollingEndpoints(journeyId),
    keepPreviousData: true,
    onSuccess: (integrations) => {
      options?.onSuccess?.(integrations);
    },
  });
};

export const useValidateJqQuery = () => {
  return useMutation({
    mutationFn: validateJqQuery,
  });
};

export const useGenerateJqQuery = () => {
  return useMutation({
    mutationFn: generateJqQuery,
  });
};

export const useResubscribeMarketplaceWebhook = (shortId: string) => {
  const { invalidateWebhook, invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: () => resubscribeMarketplaceWebhook(shortId),
    onSuccess: () => {
      invalidateWebhook(shortId);
      invalidateIntegrations();
    },
  });
};

const locale = {
  en: {
    pollVisibilitySuccess: 'Polling visibility updated successfully',
  },
};
